import React, { useState, useEffect, useCallback } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Button, Typography, CircularProgress, 
  Box, Alert, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useKeyboard } from '../../context/KeyboardContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const NewsletterDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '' });
  const [errors, setErrors] = useState({ name: '', email: '' });
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [inactivityTimer, setInactivityTimer] = useState(null);
  const kb = useKeyboard();
  
  const handleClose = useCallback(() => {
    if (inactivityTimer) clearTimeout(inactivityTimer);
    setForm({ name: '', email: '' });
    setStatus({ type: null, message: '' });
    // Suppress keyboard reopen briefly to avoid popping up on underlying page focus
    try { kb?.suppress && kb.suppress(1000); } catch {}
    try { kb?.close(); } catch {}
    try { if (document?.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur(); } catch {}
    onClose();
  }, [inactivityTimer, onClose]);

  // Reset inactivity timer on any interaction
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer) clearTimeout(inactivityTimer);
    if (open && !status.type) {
      const timer = setTimeout(() => {
        handleClose();
      }, 120000); // 2 minutes
      setInactivityTimer(timer);
    }
  }, [open, status.type, handleClose, inactivityTimer]);
  
  // Set up and clean up the inactivity timer
  useEffect(() => {
    if (open) {
      resetInactivityTimer();
    }
    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
    };
  }, [open, resetInactivityTimer]);

  useEffect(() => {
    let timer;
    if (status.type) {
      timer = setTimeout(() => {
        try { kb?.suppress && kb.suppress(1000); } catch {}
        onClose();
        setTimeout(() => setStatus({ type: null, message: '' }), 300);
      }, 15000);
    }
    return () => clearTimeout(timer);
  }, [status, onClose]);

  const validateEmail = (val) => {
    const value = String(val || '').trim();
    // Basic but robust email pattern (no spaces, one @, at least one dot with 2+ TLD chars)
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return re.test(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate name presence and email format (both required by API)
    if (!form.name || !String(form.name).trim()) {
      setErrors(prev => ({ ...prev, name: 'Please enter your name.' }));
      return;
    }
    if (!form.email || !validateEmail(form.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com).' }));
      return;
    }
    setErrors({ name: '', email: '' });
    resetInactivityTimer(); // Reset timer on form submission
    
    setIsLoading(true);
    const url = `${API_BASE_URL}/api/getresponse/contact`;
    // Include both required fields
    const payload = {
      name: String(form.name).trim(),
      email: String(form.email).trim()
    };
    
    console.log('Attempting to submit form to:', url);
    console.log('Payload:', JSON.stringify(payload, null, 2));
    
    try {
      console.log('Sending request...');
      const response = await axios.post(url, payload);
      console.log('Response received:', response.data);
      
      setStatus({ type: 'success', message: 'Thank you for subscribing to our newsletter!' });
      // Ensure keyboards are closed and focus removed
      try { kb?.close(); } catch {}
      try { if (document?.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur(); } catch {}
    } catch (error) {
      console.error('Error details:', {
        message: error.message,
        response: {
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers
        },
        request: error.request ? 'Request was made but no response received' : 'No request was made',
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      
      // Handle specific status codes
      let message = 'Failed to subscribe. Please try again.';
      let messageType = 'error';
      
      if (error.response?.status === 201) {
        message = 'Thank you for subscribing to our newsletter!';
        messageType = 'success';
      } else if (error.response?.status === 409) {
        message = 'Thank you! You are already subscribed to our newsletter.';
        messageType = 'success';
      } else if (typeof error.response?.data?.message === 'string') {
        // If backend wrongly mandates name, rewrite to reflect that only email is required
        const raw = error.response.data.message;
        if (/name\s+and\s+email\s+are\s+required/i.test(raw) || /name\s+is\s+required/i.test(raw)) {
          message = 'Please enter a valid email address.';
        } else {
          message = raw;
        }
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      }
      
      setStatus({ 
        type: messageType,
        message: message,
        details: error.response?.data?.details
      });
    } finally {
      setIsLoading(false);
      // Defensive: close any lingering keyboards and blur focus
      try { kb?.suppress && kb.suppress(1000); } catch {}
      try { kb?.close(); } catch {}
      try { if (document?.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur(); } catch {}
    }
  };

  // Virtual keyboard openers
  const openNameKeyboard = useCallback(() => {
    if (!kb) return;
    // If virtual keyboard is disabled, allow physical keyboard input to work normally
    if (kb.keyboardDisabled) {
      console.log('[NewsletterDialog] Virtual keyboard disabled, physical keyboard will work normally');
      return;
    }
    kb.open(
      'alpha',
      { showEnter: false, position: 'below', anchorSelector: '[data-kb-anchor="newsletter-dialog"]', widthPct: 0.9, heightVh: 35, autoHeight: true },
      {
        onInput: (ch) => setForm(prev => ({ ...prev, name: (prev.name || '') + ch })),
        onBackspace: () => setForm(prev => ({ ...prev, name: (prev.name || '').slice(0, -1) })),
        // Enter on Name: close keyboard and focus Email field to open its keyboard via onFocus
        onEnter: () => { 
          try { kb.close(); } catch {}
          setTimeout(() => {
            const el = document.querySelector('[data-newsletter-email="true"]');
            if (el && typeof el.focus === 'function') el.focus();
          }, 0);
        },
        onClear: () => setForm(prev => ({ ...prev, name: '' }))
      }
    );
  }, [kb]);

  const openEmailKeyboard = useCallback(() => {
    if (!kb) return;
    // If virtual keyboard is disabled, allow physical keyboard input to work normally
    if (kb.keyboardDisabled) {
      console.log('[NewsletterDialog] Virtual keyboard disabled, physical keyboard will work normally');
      return;
    }
    kb.open(
      'email',
      { showEnter: false, position: 'below', anchorSelector: '[data-kb-anchor="newsletter-dialog"]', widthPct: 0.9, heightVh: 35, autoHeight: true },
      {
        onInput: (ch) => setForm(prev => ({ ...prev, email: (prev.email || '') + ch })),
        onBackspace: () => setForm(prev => ({ ...prev, email: (prev.email || '').slice(0, -1) })),
        // Enter on Email: submit the form
        onEnter: () => { 
          try { kb.close(); } catch {}
          setTimeout(() => {
            const btn = document.querySelector('[data-newsletter-submit="true"]');
            if (btn) {
              btn.click();
            }
            try { if (document?.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur(); } catch {}
          }, 0);
        },
        onClear: () => setForm(prev => ({ ...prev, email: '' }))
      }
    );
  }, [kb]);


  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        'data-kb-anchor': 'newsletter-dialog',
        sx: {
          borderRadius: 2,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          position: 'relative',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #1976d2 0%, #21c1d6 100%)',
          }
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2, 
        pt: 3,
        position: 'relative',
        '& .MuiTypography-root': {
          fontWeight: 600,
          fontSize: '1.5rem',
          color: '#2d3748',
          textAlign: 'center',
        }
      }}>
        {status.type ? '🎉 Thank You!' : '📰 Subscribe to Our Newsletter'}
        <IconButton 
          onClick={handleClose} 
          sx={{ 
            position: 'absolute', 
            right: 12, 
            top: 12,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit} data-newsletter-form="true">
        <DialogContent sx={{ py: 3 }}>
          <Typography 
            variant="subtitle1" 
            color="textSecondary" 
            align="center" 
            gutterBottom
            sx={{
              fontSize: '1.05rem',
              lineHeight: 1.6,
              mb: 3,
              color: '#4a5568',
              maxWidth: '90%',
              mx: 'auto'
            }}
          >
            Stay connected to ICCT—events, services, and announcements.
          </Typography>
          
          {status.type ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert 
                severity={status.type}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  '& .MuiAlert-message': {
                    fontSize: '1rem',
                    py: 1,
                    color: status.type === 'success' ? '#1a365d' : '#9b2c2c'
                  },
                  backgroundColor: status.type === 'success' ? '#ebf8ff' : '#fff5f5',
                  border: `1px solid ${status.type === 'success' ? '#90cdf4' : '#feb2b2'}`
                }}
              >
                {status.message}
              </Alert>
              <Button 
                onClick={handleClose} 
                variant="contained" 
                color="primary"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: '0 4px 6px -1px rgba(25, 118, 210, 0.2)',
                  '&:hover': {
                    boxShadow: '0 10px 15px -3px rgba(25, 118, 210, 0.3)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Close
              </Button>
            </Box>
          ) : (
            <Box sx={{ maxWidth: '90%', mx: 'auto' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                Both Name and Email are required.
              </Typography>
              <TextField
                autoFocus
                margin="normal"
                label="Name"
                fullWidth
                required
                value={form.name}
                onChange={(e) => {
                  setForm({...form, name: e.target.value});
                  setErrors(prev => ({ ...prev, name: '' }));
                  resetInactivityTimer();
                }}
                onFocus={openNameKeyboard}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4299e1',
                      boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.2)'
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#718096',
                  },
                }}
                InputProps={{ readOnly: !kb?.keyboardDisabled }} // Only readonly when virtual keyboard is enabled
                error={!!errors.name}
                helperText={errors.name || ''}
              />
              <TextField
                margin="normal"
                label="Email Address"
                type="email"
                fullWidth
                required
                value={form.email}
                onChange={(e) => {
                  setForm({...form, email: e.target.value});
                  // Live-validate and clear error as user types
                  const v = e.target.value;
                  setErrors(prev => ({ ...prev, email: v && validateEmail(v) ? '' : prev.email }));
                  resetInactivityTimer();
                }}
                onFocus={openEmailKeyboard}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4299e1',
                      boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.2)'
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#718096',
                  },
                }}
                error={!!errors.email}
                helperText={errors.email || ''}
                InputProps={{ readOnly: !kb?.keyboardDisabled, inputProps: { 'data-newsletter-email': 'true' } }} // Only readonly when virtual keyboard is enabled
              />
            </Box>
          )}
        </DialogContent>
        {!status.type && (
          <DialogActions sx={{ 
            px: 3, 
            pb: 3, 
            pt: 0,
            justifyContent: 'space-between',
            '& > *': {
              m: '0 !important'
            }
          }}>
            <Button 
              onClick={handleClose} 
              sx={{
                color: '#718096',
                fontWeight: 500,
                px: 3,
                py: 1,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#f7fafc',
                  color: '#2d3748',
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              color="primary" 
              variant="contained" 
              disabled={isLoading}
              data-newsletter-submit="true"
              onMouseDown={() => {
                try { kb?.close(); } catch {}
              }}
              onClick={() => {
                try { kb?.suppress && kb.suppress(500); } catch {}
              }}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0 4px 6px -1px rgba(25, 118, 210, 0.2)',
                '&:hover': {
                  boxShadow: '0 10px 15px -3px rgba(25, 118, 210, 0.3)',
                },
                '&:active': {
                  transform: 'translateY(0)'
                },
                transition: 'all 0.2s ease-in-out',
                '&.Mui-disabled': {
                  backgroundColor: '#e2e8f0',
                  color: '#a0aec0',
                  boxShadow: 'none'
                }
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Subscribe Now'
              )}
            </Button>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
};

export default NewsletterDialog;
